import { CommonModule } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	DestroyRef,
	OnInit,
	Output,
	signal,
	inject,
	ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, from, of } from 'rxjs';
import { concatMap, map, tap, catchError } from 'rxjs/operators';

import { NotificationDetailsMainComponent } from '@shure/cloud/shared/notifications/feature-notification-details';
import {
	NotificationsApiService,
	NotificationsService,
	PostNotificationsRequest
} from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import {
	CHUNK_SIZE,
	NotificationFilterEnum,
	NotificationStoreData
} from '@shure/cloud/shared/notifications/shared/models/notification-models';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { ClickOutsideDirective, LoaderModule } from '@shure/cloud/shared/ui/components';
import { CloudNavigationService } from '@shure/cloud/shared/utils/navigation';
import { PrismProgressIndicatorComponent, PrismSnackbarService } from '@shure/prism-angular-components';
import {
	PrismButtonComponent,
	PrismCardComponent,
	PrismDialogService,
	PrismIconButtonComponent,
	PrismListComponent,
	PrismListItemComponent
} from '@shure/prism-angular-components';
import { getConvertedDate, getConvertedDateTime, updateDateFormat } from '@shure/shared/angular/ui/utils/dates';
import { asLabel } from '@shure/shared/models';

@Component({
	selector: 'sh-notifications-popup',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslocoModule,
		PrismButtonComponent,
		PrismProgressIndicatorComponent,
		LoaderModule,
		PrismCardComponent,
		ClickOutsideDirective,
		PrismIconButtonComponent,
		PrismListComponent,
		PrismListItemComponent
	],
	templateUrl: './notifications-popup.component.html',
	styleUrl: './notifications-popup.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsPopupComponent implements OnInit {
	private readonly translocoService = inject(TranslocoService);
	private readonly snackBarService = inject(PrismSnackbarService);
	private readonly organizationStore = inject(OrganizationsStoreService);
	private readonly navigationService = inject(CloudNavigationService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly notificationsStore = inject(NotificationsStore);
	private readonly router = inject(Router);
	private readonly notificationsApiService = inject(NotificationsApiService);
	private readonly notificationsService = inject(NotificationsService);
	private readonly dialogService = inject(PrismDialogService);

	@Input() public application: string | undefined = '';
	@Output() public closeNotificationPopup = new EventEmitter();

	public isFirstClick = signal(true);
	public notifications$: Observable<NotificationStoreData[] | null> = EMPTY;
	public currentDate = new Date();
	public currentDateLabel = '';
	public isApiCallFailed = false;
	public readNotifications: Array<Pick<NotificationStoreData, 'id'> & Partial<NotificationStoreData>> = [];
	public successApiCallCount = 0;
	public notificationsData: NotificationStoreData[] = [];
	public nextPageToken?: string;
	public totalNotificationsCount = 0;
	public unReadNotificationsCalled = signal(false);
	public timeZone!: string | undefined;
	public displayDateFormat!: string;
	public isLoading = signal<boolean>(true);
	public unReadNotificationsDataLength = signal<number>(0);
	private readonly translationKeyMap = new Map<string, string>([
		['unable-to-mark-as-read', asLabel('cloud.shared.notification.unable-to-mark-as-read')],
		[
			'unable-to-mark-some-notifications-as-read',
			asLabel('cloud.shared.notification.unable-to-mark-some-notifications-as-read')
		]
	]);

	/**
	 * Initializes the component and subscribe to the notifications store.
	 */
	public ngOnInit(): void {
		this.initializeUserPreferences();
		this.subscribeToUnreadNotifications();
		this.loadUnreadNotifications();
	}

	/**
	 * Handle table scroll event
	 * @param event represents Event
	 */
	public onNotificationListScroll(event: Event): void {
		const targetElement = event.target as HTMLElement;
		const notificationListViewHeight = targetElement.offsetHeight;
		const notificationListScrollHeight = targetElement.scrollHeight;
		const scrollLocation = targetElement.scrollTop;
		const buffer = 100;
		const limit = notificationListScrollHeight - notificationListViewHeight - buffer;
		if (scrollLocation > limit && this.nextPageToken && !this.isLoading() && this.totalNotificationsCount < 500) {
			this.isLoading.set(true);
			this.loadUnreadNotifications(this.nextPageToken);
		}
	}

	/**
	 * Fetches notifications from the server and updates the notification store.
	 */
	public loadUnreadNotifications(nextPageToken?: string): void {
		const params = {
			application: this.application,
			next: nextPageToken,
			filter: NotificationFilterEnum.UNREAD
		};

		this.notificationsApiService
			.getNotifications(params)
			.pipe(
				tap((notificationResponse) => {
					if (notificationResponse?.items) {
						this.nextPageToken = notificationResponse.next;

						const unreadNotifications = notificationResponse.items;

						this.notificationsStore.prependOrPatchNotifications(
							unreadNotifications as NotificationStoreData[]
						);

						this.totalNotificationsCount += unreadNotifications.length;

						this.unReadNotificationsCalled.set(true);
						this.isLoading.set(false);
					}
				}),
				catchError(() => {
					this.isApiCallFailed = true;
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	public clickedOutside(event: boolean): void {
		this.isFirstClick.set(!!event);
		if (event) {
			this.closeNotificationPopup.emit();
		}
	}

	/**
	 * Closes all dialogs and navigates to the notifications details page.
	 */
	public notificationDetails(): void {
		this.dialogService.closeAll();
		this.router.navigateByUrl('notifications-details');
	}

	/**
	 * Opens a dialog with details of the selected notification.
	 * @param notification The notification to display details for
	 */
	public openPopup(notification: NotificationStoreData): void {
		this.dialogService.closeAll();
		const selectedNotification = this.notificationsData.find((item) => item.id === notification.id);
		// Define the notificationId based on the type of notification passed

		this.dialogService.openDialog(NotificationDetailsMainComponent, {
			data: { notificationData: selectedNotification, isListPage: false }
		});
	}

	/**
	 * Marks all unread notifications as read.
	 */
	public clearAll(): void {
		this.notificationsService
			.getAllUnreadNotifications()
			.pipe(
				map((notifications) => notifications.map((notification) => notification.id)),
				map((ids) => this.chunkIds(ids)),
				concatMap((chunks) =>
					from(chunks).pipe(
						concatMap((chunk) => {
							const requestData = {
								body: chunk.map((id) => ({
									id,
									isRead: true
								}))
							};

							const readNotifications = chunk.map((id) => ({
								id,
								readAt: this.notificationsService.getCurrentDateInUTC()
							}));
							return this.notificationsApiService.postNotifications(requestData).pipe(
								tap(() => {
									this.notificationsStore.prependOrPatchNotifications(readNotifications);
								})
							);
						})
					)
				),
				catchError(() => {
					this.displayErrorMessage(this.translationKeyMap.get('unable-to-mark-as-read') ?? '');
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	public markAsRead(notificationData: NotificationStoreData): void {
		this.successApiCallCount = 0;
		const requestData = {
			body: [
				{
					id: notificationData.id,
					isRead: true
				}
			]
		};
		const readNotification = [
			{
				id: notificationData.id,
				readAt: this.notificationsService.getCurrentDateInUTC()
			}
		];
		this.updateNotifications(requestData, readNotification);
	}

	/**
	 * Post API call for updating notifications status.
	 */
	public updateNotifications(
		requestData: { body: PostNotificationsRequest },
		updatedNotifications: Array<Pick<NotificationStoreData, 'id'> & Partial<NotificationStoreData>>
	): void {
		this.notificationsApiService
			.postNotifications(requestData)
			.pipe(
				tap(() => {
					this.successApiCallCount += 1;
					this.notificationsStore.prependOrPatchNotifications(updatedNotifications);
				}),
				catchError(() => {
					const translationKey =
						this.successApiCallCount === 0
							? this.translationKeyMap.get('unable-to-mark-as-read')
							: this.translationKeyMap.get('unable-to-mark-some-notifications-as-read');
					this.displayErrorMessage(translationKey ?? '');

					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Closes all open dialogs.
	 */
	public closedialog(): void {
		this.closeNotificationPopup.emit();
		this.dialogService.closeAll();
	}

	/**
	 * Closes all dialogs and navigates to the notifications list page.
	 */
	public notificationsList(): void {
		this.closeNotificationPopup.emit();
		this.dialogService.closeAll();
		this.navigationService.toShureCloudUrl('/notifications-list');
	}

	private subscribeToUnreadNotifications(): void {
		this.notificationsStore.unReadNotifications$
			.pipe(
				tap((unreadNotifications) => {
					this.notificationsData = unreadNotifications;
					this.unReadNotificationsDataLength.set(this.notificationsData.length);

					this.notifications$ = of(
						unreadNotifications.map((item) => ({
							...item,
							body: item.body?.replace(/<[^>]*(>|$)| |‌|»|«|>/g, ' '),
							sentAt: item.sentAt
								? getConvertedDateTime(this.timeZone, this.displayDateFormat, item.sentAt)
								: ''
						}))
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	private chunkIds(ids: string[]): string[][] {
		const chunks: string[][] = [];

		for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
			chunks.push(ids.slice(i, i + CHUNK_SIZE));
		}

		return chunks;
	}

	/**
	 * Builds the popup header date using user date preferences.
	 */
	private formatCurrentDate(): string {
		return getConvertedDate(this.timeZone, this.displayDateFormat, this.currentDate.toISOString());
	}

	private initializeUserPreferences(): void {
		const accountInfo = this.organizationStore.accountInformation();

		this.timeZone = accountInfo.preferences?.timeZone || 'UTC';
		this.displayDateFormat = updateDateFormat(accountInfo.preferences?.dateFormat || 'mm/dd/yyyy');
		this.currentDateLabel = this.formatCurrentDate();
	}

	private displayErrorMessage(translationKey: string): void {
		this.snackBarService.open({
			message: this.translocoService.translate(translationKey),
			actionText: this.translocoService.translate('common.snackbar.dismiss')
		});
	}
}
